// Phase JB.2 — useTrial hook.
//
// Auth-only. Returns the user's active trial (if any), days remaining,
// and a startTrial(source) function that POSTs to jb-trial-start using
// the user's JWT. Anon → returns { trial: null }.

import { useCallback, useEffect, useId, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { trackEvent } from "./trackEvent.ts";

export type TrialSource =
  | "gap_report"
  | "paywall"
  | "first_result_email"
  | "manual"
  | "trigger";

export interface Trial {
  trial_id: string;
  user_id: string;
  started_at: string;
  expires_at: string;
  source: string;
  status: "active" | "expired" | "converted" | "cancelled";
  expired_notified_at: string | null;
}

export interface UseTrialResult {
  trial: Trial | null;
  daysRemaining: number | null;
  loading: boolean;
  error: string | null;
  startTrial: (source: TrialSource) => Promise<void>;
}

function computeDaysRemaining(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function useTrial(): UseTrialResult {
  const { user, session } = useAuthSession();
  const instanceId = useId();
  const [trial, setTrial] = useState<Trial | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setTrial(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sb.from("trials" as never) as any)
      .select("trial_id,user_id,started_at,expires_at,source,status,expired_notified_at")
      .eq("user_id", user.id)
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then((res: { data: Trial | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (res.error) setError(res.error.message);
        else setTrial(res.data ?? null);
        setLoading(false);
      });

    const channel = sb
      .channel(`trials:${user.id}:${instanceId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "postgres_changes" as any,
        { event: "*", schema: "public", table: "trials", filter: `user_id=eq.${user.id}` },
        (payload: { new: Trial }) => {
          setTrial(payload.new);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void sb.removeChannel(channel);
    };
  }, [user, instanceId]);

  const startTrial = useCallback(
    async (source: TrialSource): Promise<void> => {
      const accessToken = session?.access_token;
      if (!accessToken) {
        setError("auth_required");
        return;
      }
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url) {
        setError("config_error");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${url}/functions/v1/jb-trial-start`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ source }),
        });
        const body = (await res.json()) as { ok: boolean; trial?: Trial; error?: string };
        if (!res.ok || !body.ok || !body.trial) {
          setError(body.error ?? `http_${res.status}`);
          return;
        }
        setTrial(body.trial);
        trackEvent("trial_started", { source });
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [session?.access_token],
  );

  const activeTrial = trial?.status === "active" ? trial : null;

  return {
    trial,
    daysRemaining: activeTrial ? computeDaysRemaining(activeTrial.expires_at) : null,
    loading,
    error,
    startTrial,
  };
}
